import { Container, HStack, Box, BoxProps, useToast, Skeleton, Flex } from '@chakra-ui/react'
import Header from '../components/layouts/Header'
import Stat from '../components/UI/Stat'
import TourGuide from '../components/UX/TourGuide'
import GreetingsBanner from 'components/layouts/GreetingsBanner'
import { ERROR_TOAST } from '../constants'
import { useCountCompanyProjectsQuery, useMeQuery } from 'generated/graphql'
import React from 'react'

import PageWrap from './PageWrap'
import { useHistory } from 'react-router-dom'
import { StatType } from 'components/UI/Stat'

type PageListContainerProps = {
  title: string
  loading?: boolean
  stats?: StatType[]
  icon?: React.ReactNode
  showNoProjectBanner?: boolean
  pageActions?: React.ReactNode
  statsComponents?: React.ReactNode
  headerLeft?: React.ReactNode
  headerRight?: React.ReactNode
  withHeader?: boolean
} & BoxProps

const PageListContainer: React.FC<PageListContainerProps> = ({
  children,
  title,
  loading,
  stats,
  icon,
  pageActions,
  statsComponents,
  showNoProjectBanner,
  headerLeft,
  headerRight,
  withHeader,
  ...rest
}) => {
  const toast = useToast()

  const { data: projects, refetch } = useCountCompanyProjectsQuery({
    onError: () => {
      toast({ title: 'There was an error.', ...ERROR_TOAST })
    }
  })
  const numberOfProjects = projects?.countCompanyProjects || 0
  const history = useHistory()

  const { data: meData } = useMeQuery()

  // Legacy (non-guided) accounts that haven't confirmed their email still finish
  // on the legacy onboarding page. Guided (PIA) accounts are handled by the global
  // post-registration gate (routed to the pay gate), so they must never be sent to
  // the legacy flow from here.
  React.useEffect(() => {
    const attributes = meData?.myUser?.attributes
    if (!attributes) return

    const isGuidedSignup =
      (attributes.onboardingInformation as any)?.onboardingChannel === 'automated'

    if (
      !isGuidedSignup &&
      !attributes.confirmed &&
      history.location.pathname !== '/auth/onboarding'
    ) {
      history.push('/auth/onboarding')
    }
  }, [meData])

  React.useEffect(() => {
    ;(async () => await refetch())()
  }, [history.location.pathname])

  return (
    <>
      {withHeader && <Header />}
      <PageWrap direction="column" title={title} align="center" pt={withHeader ? undefined : 0}>
        <Box pb={20} pt={withHeader ? 0 : 8} flex={1} w="100%" {...rest}>
          <Container position="relative" maxW="8xl" px={{ base: 4, md: 6, lg: 8 }}>
            <Flex
              mb={4}
              align="center"
              justify={pageActions ? 'space-between' : 'flex-end'}
              flexDir={{ base: 'column', md: 'row' }}
              gap={3}
            >
              {pageActions}
              <HStack spacing={{ base: 3, md: 6 }} flexWrap="wrap">
                {stats?.map((stat) => (
                  <Skeleton isLoaded={!stat.loading} rounded="0" key={stat.title} minW="120px">
                    <Stat value={stat} type="stat" onClick={stat.onClick} />
                  </Skeleton>
                ))}
                {statsComponents}
              </HStack>
            </Flex>

            {children}
          </Container>
        </Box>
      </PageWrap>
    </>
  )
}
PageListContainer.defaultProps = {
  showNoProjectBanner: true,
  withHeader: false
}
export default PageListContainer
